import * as React from 'react'
import './style.less'
import Skeleton from 'telstra-ui/components/skeleton/Skeleton'
import {SvgIcon} from 'telstra-ui/components/svg/Icon'
import {MDUMPageWrapper} from '@applications/mobile-data-usage-meter/components/mdum-page-wrapper/mdum-page-wrapper'
import {NPErrorBoundary} from '@components/error-boundary'
import {CONSTANTS} from '@applications/mobile-data-usage-meter/utils/enum-constants'
import {DefaultInternationalUsageBarLineChart} from './international-chart'
import {getMDUMFlags} from '@telstra-connect/mobile-data-usage-meter/feature-flags'

const NUMBER = 'Service ID'
const SIM = 'SIM serial number'
const PLAN = 'Plan name'
const BILLING_NUMBER = 'Billing account number'

const SkeletonScreen = () => <Skeleton count={1} height="20px" width="140px" />

const SubStickyDetailsSection = () => (
  <div className="tc-mobility-detail-page-header-details">
    <div className="tc-mobility-header-details-sub-container">
      <div className="header-title header-width">
        <SvgIcon name="icon-mobiles-mobile" className="custom-svg-class text-regular" />
        <span className="label-margin heading16"> {NUMBER} </span>
        <div>
          <div className="body16 label-substicky-text">
            <SkeletonScreen />
          </div>
        </div>
      </div>
      <div className="header-title header-width">
        <SvgIcon name="icon-mobiles-plan" className="custom-svg-class text-regular" />
        <span className="label-margin heading16"> {SIM} </span>
        <div>
          <div className="body16 label-substicky-text">
            <SkeletonScreen />
          </div>
        </div>
      </div>
      <div className="header-title header-width">
        <SvgIcon name="icon-mobiles-plan" className="custom-svg-class text-regular" />
        <span className="label-margin heading16"> {PLAN} </span>
        <div>
          <div className="body16 label-substicky-text">
            <SkeletonScreen />
          </div>
        </div>
      </div>
      <div className="header-title header-width">
        <SvgIcon className="custom-svg-class text-regular" name="icon-bills-billing" />
        <span className="label-margin heading16">{BILLING_NUMBER}</span>
        <div>
          <div className="body16 label-substicky-text">
            <SkeletonScreen />
          </div>
        </div>
      </div>
    </div>
  </div>
)

const GraphHeaderSection = ({headerLabel}) => {
  return (
    <React.Fragment>
      <div className="tc-mobility-header-container">
        <div className="tc-mobility-graph-row-padding">
          <div className="heading16">{headerLabel}</div>
        </div>
        <div className="tc-mobility-last-updated-time">
          <div className="body14-light">{''}</div>
        </div>
      </div>
    </React.Fragment>
  )
}

const UsageInfoBox = () => {
  const row1BigText = <Skeleton width="140px" height="32px" count="1" />
  const row1smallText = null
  const row2 = <Skeleton width="190px" height="20px" count="1" />

  return (
    <div className="tc-mobility-usage-info-box">
      <div>
        <span className="tc-mobility-font-size-forty">{row1BigText}</span>
        <span className="body14"> {row1smallText}</span>
      </div>
      <div className="body14">{row2}</div>
    </div>
  )
}

const DataUsagesComponent = ({
  header,
  used,
  units,
  total,
  percentage,
  remaining,
  classLoader,
  dailyAverage,
}) => {
  return (
    <div className="row tc-int-usage-subblock">
      <div className="col-md-12 ">
        <div className="text-bold">{header}</div>
        <div className="row tc-int-usage-data-use">
          <div className="col-xs-6">
            <span className="body18">
              {used}
              {units}
              {[' hour', ' hours', ' days', ' day'].includes(units) ? ' elapsed' : ' used'}
            </span>
          </div>
          <div className="col-xs-6">
            <div className="body14 tc-mobility-progressbar-above__right-section">
              {total ? `/${total} ` : ''}
            </div>
          </div>
        </div>
        <ProgressBar barcolorclass={classLoader} percentage={percentage} />
        <div className="tc-mobility-progressbar-down-value">
          <div className="body14-light">{remaining}</div>
          <div className="body14-light">{dailyAverage}</div>
        </div>
      </div>
    </div>
  )
}

const ProgressBar = (props) => {
  return (
    <div className="outside">
      <div className={props.barcolorclass} style={{width: `${props.percentage}%`}} />
    </div>
  )
}

const AustralianUsage = () => {
  const AUSTRALIAN_DATA_USAGE = 'Australian data usage'
  const defaultUsageAllowanceData = [
    {
      trafficOrigin: CONSTANTS.TRAFFIC_ORIGIN.DOMESTIC,
      allowanceType: CONSTANTS.ALLOWANCE_TYPE.STANDARD,
      trafficType: CONSTANTS.TRAFFIC_TYPE.DATA,
    },
  ]

  return (
    <>
      {defaultUsageAllowanceData
        ?.filter(
          (item) =>
            item?.trafficOrigin === CONSTANTS.TRAFFIC_ORIGIN.DOMESTIC &&
            item?.allowanceType === CONSTANTS.ALLOWANCE_TYPE.STANDARD &&
            item?.trafficType === CONSTANTS.TRAFFIC_TYPE.DATA,
        )
        .map((_, i) => {
          const dataUsage = {
            header: 'Data',
            used: '',
            units: 'GB',
            total: 'GB',
            percentage: 0,
            remaining: 'GB remaining',
            classLoader: 'loader',
            dailyAverage: '',
          }
          const daysRemaining = {
            header: 'Days',
            used: '',
            units: ' days',
            total: '',
            percentage: 0,
            remaining: ' days remaining',
            classLoader: 'loader3',
            dailyAverage: null,
          }

          return (
            <div key={i} className="mobility-aus-main-container">
              <GraphHeaderSection headerLabel={AUSTRALIAN_DATA_USAGE} />
              <div className="row">
                <div className="mobility-upper-part-contaner">
                  <div className="mobility-used-data-style">{<UsageInfoBox />}</div>
                  <div className="mobility-used-data-line-graph">
                    <DataUsagesComponent {...dataUsage} />
                  </div>
                </div>
                <div className="mobility-lower-part-contaner">
                  <div className="mobility-remaining-days-style">{<UsageInfoBox />}</div>
                  <div className="mobility-remaining-days-line-graph">
                    <DataUsagesComponent {...daysRemaining} />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
    </>
  )
}

const ViewCountriesLink = () => {
  const VIEW_COUNTRIES_AND_PRICING = 'View countries and pricing'
  const VIEW_COUNTRIES_AND_PRICING_LINK = getMDUMFlags()?.viewCountriesAndPricingLink
  return (
    <div className="body14 data-align tc-mobility-int-usage-chart-countries-pricing">
      <a href={VIEW_COUNTRIES_AND_PRICING_LINK} target="_blank" rel="noopener noreferrer">
        {VIEW_COUNTRIES_AND_PRICING}
      </a>
    </div>
  )
}

const InternationalUsage = () => {
  const INTERNATIONAL_USAGE = 'International usage'
  return (
    <div className="tc-mobility-int-usage-chart-container">
      <GraphHeaderSection headerLabel={INTERNATIONAL_USAGE} />
      <div className="row tc-mobility-int-usage-chart-inner-container">
        <div className="tc-mobility-int-usage-chart-left-side">
          <UsageInfoBox />
          <ViewCountriesLink />
        </div>
        <div className="tc-mobility-int-usage-chart-bar-line-graph">
          <DefaultInternationalUsageBarLineChart />
        </div>
      </div>
    </div>
  )
}

export const LazyLoadingComponent = () => {
  return (
    <NPErrorBoundary>
      <MDUMPageWrapper pageClassName="view-service-details">
        <div className="tc-mobility-service-details">
          <div className="tc-mobility-service-details-lazy-container">
            <div className="tc-mobility-lazy-breadcrumbs">
              <SkeletonScreen />
            </div>
            <div className="tc-mobility-lazy-section-title">
              <SkeletonScreen />
            </div>
            <div className="tc-mobility-lazy-details-header">
              <SkeletonScreen />
            </div>
            <div className="tc-mobility-lazy-substicky-details-container">
              <SubStickyDetailsSection />
            </div>
            <div className="tc-mobility-lazy-aus-usage-container">
              <AustralianUsage />
            </div>
            <div className="tc-mobility-lazy-int-usage-container">
              <InternationalUsage />
            </div>
          </div>
        </div>
      </MDUMPageWrapper>
    </NPErrorBoundary>
  )
}
